import { ComponentType, FC, PropsWithChildren, PureComponent, ReactNode, createContext, useContext, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { persistQueryClient } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { UserContext } from "@app/user/UserContext";
import Features from "@app/feature-flags/features";
import FeatureFlagService from "@app/feature-flags/featureFlagService";

export interface FeatureFlagContextProps {
    enabledFeatureFlags: Set<string>;
    featureFlagsLoading?: boolean;
}

export const FeatureFlagContext = createContext<FeatureFlagContextProps>({
    enabledFeatureFlags: new Set<string>(),
    featureFlagsLoading: false,
});

export const FeatureFlagContextProvider: FC<{ children: ReactNode }> = (props: { children: ReactNode }) => {
    const user = useContext(UserContext);
    const queryClient = useQueryClient();
    const service = useRef(new FeatureFlagService()).current;

    useEffect(() => {
        if (!user?.userId) return;

        const persister = createAsyncStoragePersister({
            key: `QUERY_CACHE_${user.userId}`,
            storage: {
                getItem: (key) => window.localStorage.getItem(key),
                removeItem: (key) => window.localStorage.removeItem(key),
                setItem: (key, value) => window.localStorage.setItem(key, value),
            },
        });

        const [unsubscribe] = persistQueryClient({
            dehydrateOptions: {
                shouldDehydrateQuery: (query) => query.queryKey[0] === "featureFlags" && query.queryKey[1] === user.userId,
            },
            maxAge: 5 * 60 * 1000,
            persister,
            queryClient,
        });

        return unsubscribe;
    }, [user?.userId, queryClient]);

    const { data, isPending } = useQuery({
        enabled: !!user?.userId,
        gcTime: 5 * 60 * 1000,
        queryFn: async ({ signal }) => Array.from(await service.getAllEnabledFeatures(signal)),
        queryKey: ["featureFlags", user?.userId],
        staleTime: 30 * 1000,
    });

    const enabledFeatureFlags = new Set<Features>(data ?? []);

    return (
        <FeatureFlagContext.Provider value={{ enabledFeatureFlags, featureFlagsLoading: isPending }}>
            {props.children}
        </FeatureFlagContext.Provider>
    );
};

export function withFeatureFlagContext<T>(Component: ComponentType<PropsWithChildren<T & FeatureFlagContextProps>>): ComponentType<PropsWithChildren<T>> {
    class WithFeatureFlagContext extends PureComponent<PropsWithChildren<T>> {
        public render() {
            return (
                <FeatureFlagContext.Consumer>
                    {({ enabledFeatureFlags, featureFlagsLoading }) => (
                        <Component {...this.props} enabledFeatureFlags={enabledFeatureFlags} featureFlagsLoading={featureFlagsLoading} />
                    )}
                </FeatureFlagContext.Consumer>
            );
        }
    }

    return WithFeatureFlagContext;
}
